import { Data, Match } from "effect";
import type { AuditEvent } from "./audit.ts";
import type { AnnotationId } from "./ids.ts";

// Operational sentinels for a manual annotation's `rule`, not pedagogy, so they live in code
// (CLAUDE.md heuristic). Historical: `MANUAL_UNCLASSIFIED` marked a manual annotation awaiting the
// classify pass; `MANUAL_RULE_GAP` marked one the classify pass could not pin to any rule. The
// classify pass (and the rubric-refinement loop it fed) is retired (ADR-0044) — a manual
// (Expert-authored) annotation is now an ordinary open finding, never classified (CONTEXT.md).
// Neither historical constant is written by any *new* machinery in this workspace, but both remain
// exported: existing documents and append-only audit logs already carry these `manual:*` rule
// strings, and every reader (parsers, folds, projections, the web review-sidebar's rule bucketing)
// must keep recognising them.
export const MANUAL_UNCLASSIFIED = "manual:unclassified";
export const MANUAL_RULE_GAP = "manual:rule-gap";

// The canonical `rule` value the server stamps on every *new* manual annotation (ADR-0044, moved
// here from `apps/server/src/rest.ts`'s local constant so the one write-time sentinel lives
// alongside its historical read-only siblings above). It carries no routing meaning of its own — a
// manual finding's identity is `author === "expert"` (`isManualAnnotation`), never its `rule`
// string — it exists only to satisfy `isManualSentinel`'s `manual:` prefix, which
// `write-annotation`'s catalog check exempts, so the Expert's free-text finding never has to cite a
// real rubric rule id.
export const MANUAL_FINDING_RULE = "manual:finding";

// A `manual:*` sentinel is an operational marker, not a rubric citation, so it is exempt from
// catalog validation (it has no catalog rule to match). Keyed on the shared prefix so both
// sentinels above — and any future `manual:*` — are covered. Mirrors the pass-through that
// `canonicalizeRuleId` already gives the same prefix.
export const isManualSentinel = (rule: string): boolean => rule.startsWith("manual:");

// "Is this manual?" is `author === "expert"` (CONTEXT.md) — never inferred from the rule string.
// A manual annotation carries no classification and is never reclassified (the classify pass is
// retired), so this predicate is the sole authority on manual-vs-judge identity.
//
// It is also the openness filter for `annotation.created.v2` (ADR-0128): only an Expert's own finding
// is open work. Every other author is an automated pass — the retired check suite or the retired agent
// `annotate` tool — and nothing can retract one any more, so the append-only log keeps it as history
// but no openness fold (`projectOpenFindings`, review-state's `applyEvent`, `projectDiagnostics`)
// reads it.
export const isManualAnnotation = (event: AuditEvent): boolean =>
  Match.value(event).pipe(
    Match.tag("annotation.created", (e) => e.author === "expert"),
    Match.tag("annotation.created.v2", (e) => e.author === "expert"),
    Match.orElse(() => false),
  );

// `strip` targets only an Expert's own manual annotation: the target must have an Expert-authored
// `created` event. A judge annotation (whose fate is a human gate) or an id with no `created` event
// is not a valid strip target. Distinct from `AlreadyDecided` (`pending.ts`), which guards a manual
// finding that is a valid strip target but is no longer open (already kept/rejected/closed).
export class NotManual extends Data.TaggedError("NotManual")<{
  readonly annotationId: AnnotationId;
}> {}

// Whether the id has an Expert-authored `created` event in the log — the precondition for
// `strip-annotation`. False for judge annotations and for ids the log has never seen created.
export const isManualTarget = (events: readonly AuditEvent[], id: AnnotationId): boolean =>
  events.some((e) =>
    Match.value(e).pipe(
      Match.tag(
        "annotation.created",
        (created) => created.annotationId === id && created.author === "expert",
      ),
      Match.tag(
        "annotation.created.v2",
        (created) => created.annotationId === id && created.author === "expert",
      ),
      Match.orElse(() => false),
    ),
  );
